import { useCallback, useEffect, useRef, useState } from "react";

import { isAbort } from "./api";

export interface FetchState<T> {
  data: T | null;
  loading: boolean;
  /** 이미 표시 중인 데이터를 유지한 채 재조회하는 중. */
  refreshing: boolean;
  error: string | null;
  /** 마지막 성공 시각(epoch ms). */
  updatedAt: number | null;
  refresh: () => void;
}

interface Options {
  /** 탭이 다시 보이거나 온라인으로 복귀할 때 재조회. */
  revalidateOnFocus?: boolean;
  /** 주기적 재조회(ms). 탭이 숨겨져 있으면 건너뛴다. */
  intervalMs?: number;
}

/**
 * 데이터 패칭 훅(로딩/에러/재조회).
 *
 * 브라우저에서는 필터를 빠르게 연타할 수 있어 이전 요청을 `AbortController` 로
 * 취소한다 — 늦게 도착한 오래된 응답이 최신 화면을 덮어쓰지 않게 하기 위함이다.
 */
export function useFetch<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[] = [],
  options: Options = {},
): FetchState<T> {
  const { revalidateOnFocus = false, intervalMs } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  // deps 가 바뀔 때만 fetcher 를 새로 고정한다.
  const memoFetcher = useCallback(fetcher, deps);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const inFlightRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (isRefresh: boolean) => {
      inFlightRef.current?.abort();
      const controller = new AbortController();
      inFlightRef.current = controller;

      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      // 최신 요청이고 컴포넌트가 살아 있을 때만 상태를 반영한다.
      const current = () => aliveRef.current && inFlightRef.current === controller;

      try {
        const next = await memoFetcher(controller.signal);
        if (!current()) return;
        setData(next);
        setUpdatedAt(Date.now());
      } catch (e) {
        if (!current() || isAbort(e)) return;
        setError(e instanceof Error ? e.message : "알 수 없는 오류");
      } finally {
        if (current()) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [memoFetcher],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // 언마운트 시 진행 중 요청 정리.
  useEffect(() => () => inFlightRef.current?.abort(), []);

  useEffect(() => {
    if (!revalidateOnFocus) return;
    const onWake = () => {
      if (document.visibilityState === "visible") void load(true);
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("online", onWake);
    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("online", onWake);
    };
  }, [revalidateOnFocus, load]);

  useEffect(() => {
    if (!intervalMs) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void load(true);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, load]);

  // 헤더의 새로고침 버튼이 effect 의존성으로 쓰므로 참조를 고정한다.
  const refresh = useCallback(() => void load(true), [load]);

  return { data, loading, refreshing, error, updatedAt, refresh };
}
